import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab,
  useTheme
} from '@mui/material'
import { ActionButton } from '@emerald-works-nova/components'
import {
  Close as CloseIcon,
  Home as HeroIcon,
  ViewCarousel as CarouselIcon,
  Category as ProductsIcon,
  ViewModule as BannersIcon,
  Computer as DesktopIcon,
  Smartphone as MobileIcon
} from '@mui/icons-material'
import { useStyles } from './style'
import * as previewImages from './images'

// Section type color constants - using theme-like references
const getSectionColors = (palette) => ({
  PRIMARY_BLUE: palette.primary?.main || '#1976d2',
  SUCCESS_GREEN: palette.success?.main || '#4caf50',
  WARNING_ORANGE: palette.warning?.main || '#ff9800',
  SECONDARY_PURPLE: palette.secondary?.main || '#9c27b0'
})

// Section type configurations
const getSectionTypesConfig = (colors) => ({
  mtBanner: {
    label: 'Hero Section',
    icon: HeroIcon,
    description: 'Main banner with title, subtitle, description, CTA and image',
    color: colors.PRIMARY_BLUE,
    preview: {
      desktop: previewImages.heroDesktop,
      mobile: previewImages.heroMobile
    }
  },
  featuresBanner: {
    label: 'Featured Products',
    icon: ProductsIcon,
    description: 'Grid of product cards with titles, descriptions and links',
    color: colors.SUCCESS_GREEN,
    preview: {
      desktop: previewImages.featuredProductsDesktop,
      mobile: previewImages.featuredProductsMobile
    }
  },
  contentExplorer: {
    label: 'Explore Content Carousel',
    icon: CarouselIcon,
    description: 'Content carousel section with customizable title',
    color: colors.WARNING_ORANGE,
    preview: {
      desktop: previewImages.exploreContentDesktop,
      mobile: previewImages.exploreContentMobile
    }
  },
  featureCard: {
    label: 'Product Banners',
    icon: BannersIcon,
    description: 'Promotional banners with images, titles, descriptions and CTAs',
    color: colors.SECONDARY_PURPLE,
    preview: {
      desktop: previewImages.productBannersDesktop,
      mobile: previewImages.productBannersMobile
    }
  }
})

const AddSectionModal = ({ open, onClose, onAddSection }) => {
  const classes = useStyles()
  const theme = useTheme()
  const [selectedPreview, setSelectedPreview] = useState(null)
  const [previewTab, setPreviewTab] = useState('desktop')

  // Get theme-based colors and configurations
  const sectionColors = getSectionColors(theme.palette)
  const sectionTypesConfig = getSectionTypesConfig(sectionColors)

  const availableSectionTypes = Object.entries(sectionTypesConfig).map(([type, config]) => ({
    type,
    label: config.label,
    icon: <config.icon />,
    description: config.description,
    color: config.color,
    preview: {
      desktop: config.preview.desktop,
      mobile: config.preview.mobile
    }
  }))

  const handleSectionSelect = (sectionType) => {
    onAddSection(sectionType)
    onClose()
  }

  const handlePreviewSelect = (sectionType) => {
    const section = availableSectionTypes.find(s => s.type === sectionType)
    setSelectedPreview(section)
    setPreviewTab('desktop')
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      className={classes.dialog}
      data-test='cms-add-section-modal'
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h5' className={classes.sectionTypeTitle}>
            Add New Section
          </Typography>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant='body2' color='textSecondary' className={classes.dialogSubtitle}>
          Choose a section type to add to your homepage
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          {/* Section Selection */}
          <Grid item xs={12} md={4}>
            <Box className={classes.sectionTypeHeader}>
              <Typography variant='subtitle1' className={classes.sectionTypeTitle}>
                Choose Section Type
              </Typography>
            </Box>
            <Grid container spacing={1} direction='column'>
              {availableSectionTypes.map((sectionType) => (
                <Grid item xs={12} key={sectionType.type}>
                  <Card
                    className={`${classes.sectionCard} ${classes.clickableCard} ${selectedPreview?.type === sectionType.type ? classes.selectedCard : ''}`}
                    onClick={() => handlePreviewSelect(sectionType.type)}
                    onDoubleClick={() => handleSectionSelect(sectionType.type)}
                    data-test={`section-card-${sectionType.type}`}
                  >
                    <CardContent className={classes.cardContent}>
                      <Box
                        className={classes.iconContainer}
                        style={{ '--section-type-color': sectionType.color }}
                      >
                        {sectionType.icon}
                      </Box>
                      <Box className={classes.cardTextContainer}>
                        <Typography variant='h6' className={classes.cardTitle}>
                          {sectionType.label}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='textSecondary'
                          className={classes.cardDescription}
                        >
                          {sectionType.description}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {selectedPreview && (
              <Box className={classes.addButtonContainer}>
                <ActionButton
                  variant='primary'
                  onClick={() => handleSectionSelect(selectedPreview.type)}
                  dataTest={`cms-add-section-${selectedPreview.type}`}
                >
                  Add {selectedPreview.label}
                </ActionButton>
              </Box>
            )}
          </Grid>

          {/* Preview Area */}
          <Grid item xs={12} md={8}>
            <Typography variant='subtitle1' className={classes.previewHeader}>
              Preview
            </Typography>
            <Box className={classes.previewContainer}>
              {!selectedPreview ? (
                <Box className={classes.emptyPreview}>
                  <Typography variant='h6' color='textSecondary'>
                    Preview
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    Select a section type to see preview
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Tabs
                    value={previewTab}
                    onChange={(e, newValue) => setPreviewTab(newValue)}
                    className={`${classes.previewTabs} ${classes.previewTabsContainer}`}
                    data-test='preview-tabs'
                  >
                    <Tab
                      icon={<DesktopIcon />}
                      label='Desktop'
                      value='desktop'
                      iconPosition='start'
                      data-test='preview-tab-desktop'
                    />
                    <Tab
                      icon={<MobileIcon />}
                      label='Mobile'
                      value='mobile'
                      iconPosition='start'
                      data-test='preview-tab-mobile'
                    />
                  </Tabs>
                  <Box className={classes.previewImageContainer}>
                    <img
                      src={selectedPreview.preview[previewTab]}
                      alt={`${selectedPreview.label} ${previewTab} preview`}
                      className={`${classes.previewImage} ${previewTab === 'mobile' ? classes.mobilePreviewImage : classes.desktopPreviewImage}`}
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextSibling.style.display = 'flex'
                      }}
                      onLoad={(e) => {
                        e.target.style.display = 'block'
                        e.target.nextSibling.style.display = 'none'
                      }}
                    />
                    <Box
                      className={`${classes.previewFallback} ${classes.previewFallbackContainer}`}
                    >
                      <Typography variant='body2' color='textSecondary'>
                        Preview image not available
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default AddSectionModal
